import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/categories";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://pandabuy.cv";
const articleUrl = `${siteUrl}/pandabuy-spreadsheet-guide`;

const articleJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "The Complete PandabuySpreadsheet Guide for Beginners (2026)",
  description: "A comprehensive guide to finding, using, and maximizing pandabuy spreadsheets for smarter shopping. Learn what makes a quality spreadsheet, explore 14 curated categories, and discover tips for budget-friendly fashion finds.",
  author: {
    "@type": "Organization",
    name: "PandabuySpreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "PandabuySpreadsheet",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg",
    },
  },
  datePublished: "2026-06-01",
  dateModified: "2026-07-01",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": articleUrl,
  },
  image: "https://readdy.ai/api/search-image?query=Elegant%20editorial%20fashion%20photography%20showing%20beautifully%20folded%20cream%20and%20beige%20colored%20clothing%20items%20arranged%20meticulously%20on%20soft%20linen%20surface%20with%20gentle%20natural%20window%20lighting%20warm%20ambient%20tone%20clean%20modern%20professional%20product%20photography%20subtle%20shadows%20refined%20composition%20high%20detail%20fresh%20look%20european%20boutique%20style%20sophisticated%20calm%20mood%20curated%20collection%20aesthetic&width=1600&height=900&seq=article-header-v5&orientation=landscape",
};

export default function ArticlePage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50 font-sans">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />

      <main>
        {/* Article Header */}
        <section className="relative w-full min-h-[450px] md:min-h-[550px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20editorial%20fashion%20photography%20showing%20beautifully%20folded%20cream%20and%20beige%20colored%20clothing%20items%20arranged%20meticulously%20on%20soft%20linen%20surface%20with%20gentle%20natural%20window%20lighting%20warm%20ambient%20tone%20clean%20modern%20professional%20product%20photography%20subtle%20shadows%20refined%20composition%20high%20detail%20fresh%20look%20european%20boutique%20style%20sophisticated%20calm%20mood%20curated%20collection%20aesthetic&width=1600&height=900&seq=article-header-v5&orientation=landscape"
              alt="Complete Pandabuy Spreadsheet Guide"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/35 to-black/20"></div>
          </div>

          <div className="relative z-10 w-full px-6 md:px-10 pt-20">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 text-sm text-white/60 mb-4">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-white/80">Pandabuy Spreadsheet Guide</span>
              </nav>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight">
                The Complete Pandabuy<span className="text-accent-400">Spreadsheet</span> Guide for Beginners (2026)
              </h1>
              <div className="mt-4 flex items-center gap-4 text-sm text-white/50">
                <span>Updated June 2026</span>
                <span>&middot;</span>
                <span>8 min read</span>
                <span>&middot;</span>
                <span>2,500+ words</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-16 md:py-24">
          <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
            <div className="prose-custom space-y-10">
              {/* Intro */}
              <section>
                <p className="text-lg text-foreground-700 leading-relaxed">
                  If you&apos;ve ever spent hours scrolling through endless product listings trying to find the best fashion deals,
                  you already know how frustrating unorganized shopping can be. That&apos;s exactly why the{" "}
                  <strong className="text-foreground-950">pandabuyspreadsheet</strong> system was created — to turn chaos into clarity.
                  This guide will walk you through everything you need to know about finding, using, and maximizing pandabuy spreadsheets
                  for smarter shopping. From understanding what makes a <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium underline">quality spreadsheet</Link>{" "}
                  stand out, to discovering hidden categories you didn&apos;t even know existed.
                </p>
              </section>

              {/* Section 1 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  What Is a Pandabuy Spreadsheet?
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-4">
                  A <strong>pandabuy spreadsheet</strong> is essentially a curated product discovery list — organized into rows and columns —
                  that compiles the best shopping finds across multiple categories. Think of it as a digital treasure map where every entry
                  has been vetted, rated, and categorized so you don&apos;t have to do the hard work yourself.
                </p>
                <p className="text-base text-foreground-600 leading-relaxed mb-4">
                  Unlike random product links scattered across social media, a well-maintained pandabuy spreadsheet gives you structured
                  data: product names, prices, quality ratings, category tags, and direct links. This makes comparison shopping effortless
                  and helps you avoid low-quality items. Our <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium underline">PandabuySpreadsheet Hub</Link>{" "}
                  takes this concept further by organizing thousands of finds into 14 distinct categories.
                </p>
                <p className="text-base text-foreground-600 leading-relaxed">
                  The best part? Most pandabuy spreadsheets are community-driven, meaning real shoppers contribute their finds, rate products
                  honestly, and keep lists updated. You benefit from collective experience without spending hours researching each item yourself.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Why Use a Curated Pandabuy Spreadsheet?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {[
                    { title: "Save Time", desc: "Stop scrolling endlessly. A single spreadsheet organizes hundreds of products in one place.", icon: "ri-time-line" },
                    { title: "Compare Quality", desc: "Community ratings help you identify high-quality items before purchasing.", icon: "ri-star-line" },
                    { title: "Budget Friendly", desc: "Find affordable alternatives to expensive brand-name products.", icon: "ri-money-dollar-circle-line" },
                    { title: "Stay Updated", desc: "Trending items are added daily so you never miss out on viral finds.", icon: "ri-refresh-line" },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-4 p-5 bg-white rounded-2xl border border-background-200">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <div>
                        <h3 className="font-semibold text-foreground-950 text-base">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-base text-foreground-600 leading-relaxed">
                  Whether you&apos;re a casual shopper looking for a few nice pieces or a fashion enthusiast building a complete wardrobe,
                  using a pandabuy spreadsheet transforms the way you shop online. Visit{" "}
                  <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium underline">our homepage</Link> to explore all 14 categories
                  and find exactly what you&apos;re looking for.
                </p>
              </section>

              {/* Mid-article CTA */}
              <section className="grid grid-cols-1 md:grid-cols-2 rounded-3xl overflow-hidden border border-background-200">
                <div className="relative h-[250px] md:h-auto overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20fashion%20flat%20lay%20with%20cream%20colored%20sneakers%20soft%20beige%20knitwear%20and%20warm%20toned%20accessories%20on%20textured%20linen%20surface%20natural%20morning%20daylight%20warm%20ambient%20tone%20editorial%20style%20modern%20aesthetic%20professional%20product%20photography%20fresh%20inviting%20composition%20sophisticated%20calm%20mood&width=800&height=600&seq=article-cta-v5&orientation=landscape"
                    alt="Visit Main Store"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="text-xs text-white/70 uppercase tracking-wider">Shop Now</span>
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center px-8 py-12 bg-white text-center">
                  <h3 className="text-2xl font-heading font-bold text-foreground-950">Ready to Start Shopping?</h3>
                  <p className="mt-3 text-sm text-foreground-500 leading-relaxed max-w-xs">
                    Browse the full collection of curated spreadsheets on our main store. Updated weekly with fresh finds.
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 mt-6 px-7 py-3 bg-foreground-900 text-white text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap group"
                  >
                    Visit Main Store
                    <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
                  </a>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  How to Find the Best Pandabuy Spreadsheet
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-4">
                  Not all spreadsheets are created equal. A truly valuable <strong>pandabuyspreadsheet</strong> should have several key
                  characteristics that separate it from random link dumps you might find on social media. Here&apos;s what to look for:
                </p>
                <ul className="space-y-3 mb-4">
                  {[
                    "Regular updates — The best spreadsheets are maintained daily or weekly with new product additions",
                    "Quality ratings — Look for spreadsheets that include honest community ratings, not just product links",
                    "Clear categorization — Products should be organized into logical categories like Shoes, Hoodies, T-Shirts, and Accessories",
                    "Price transparency — All prices should be clearly listed so you can compare at a glance",
                    "Direct links — Each entry should link directly to the product page for easy access",
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-foreground-600 leading-relaxed">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-base text-foreground-600 leading-relaxed">
                  Our <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium underline">PandabuySpreadsheet Hub</Link> checks
                  all these boxes and more. With 14 curated categories and hundreds of verified finds, it&apos;s designed to make your
                  shopping experience as smooth as possible.
                </p>
              </section>

              {/* Section 4 - Categories Overview */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Exploring Pandabuy Spreadsheet Categories
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  One of the biggest advantages of using a structured pandabuy spreadsheet is the category system. Instead of mixing
                  sneakers with perfume and bags with belts, everything has its own dedicated section. Here are the 14 categories
                  available on our hub:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 p-3 bg-white rounded-xl border border-background-200 hover:border-accent-200 hover:bg-accent-50/30 transition-all cursor-pointer group"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-950 transition-colors whitespace-nowrap">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
                <p className="text-base text-foreground-600 leading-relaxed">
                  Each category links directly to the corresponding section on kaspreadsheet.com, where you&apos;ll find the most
                  up-to-date product listings. From <strong>Shoes</strong> to <strong>Perfumes</strong>, every category is maintained
                  and refreshed regularly to ensure you always see the best available finds.
                </p>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Tips for Maximizing Your Pandabuy Spreadsheet Experience
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-4">
                  Once you&apos;ve found a great pandabuy spreadsheet, here are some pro tips to get the most out of it:
                </p>
                <div className="space-y-4">
                  {[
                    { num: "01", title: "Check updates regularly", desc: "The best finds often appear and sell out quickly. Make it a habit to check your favorite categories at least once a week to catch new additions before they&apos;re gone." },
                    { num: "02", title: "Cross-reference quality ratings", desc: "Don&apos;t just look at one rating. Compare multiple listings in the same category to gauge what a reasonable quality-to-price ratio looks like for each type of product." },
                    { num: "03", title: "Explore beyond your comfort zone", desc: "You might come for the sneakers, but don&apos;t overlook categories like Accessories or Bags. Some of the best budget finds live in categories you wouldn&apos;t normally browse." },
                    { num: "04", title: "Bookmark the hub", desc: "Save the PandabuySpreadsheet Hub as a browser bookmark so you can jump straight into browsing whenever you have a few spare minutes." },
                  ].map((tip) => (
                    <div key={tip.num} className="flex gap-4 p-5 bg-white rounded-2xl border border-background-200">
                      <span className="text-2xl font-heading font-bold text-accent-300 flex-shrink-0">{tip.num}</span>
                      <div>
                        <h3 className="font-semibold text-foreground-950">{tip.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{tip.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 6 - FAQ */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {[
                    { q: "What is a pandabuy spreadsheet?", a: "A pandabuy spreadsheet is a curated, organized list of shopping finds — typically fashion items, sneakers, and accessories — compiled into an easy-to-browse format with prices, quality ratings, and direct links to product pages." },
                    { q: "How often is the pandabuy spreadsheet updated?", a: "Our curated spreadsheets are updated regularly — with new trending items added weekly and quality ratings refreshed based on community feedback. Some categories see daily updates during peak shopping seasons." },
                    { q: "Is the pandabuy spreadsheet free to use?", a: "Yes! All the spreadsheets linked from our hub are completely free to access. No registration, no subscription — just browse, find what you like, and click through to shop." },
                    { q: "How many product categories are available?", a: "Currently, we organize products into 14 distinct categories: Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear/Underpants, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff." },
                    { q: "Can I contribute my own finds?", a: "The spreadsheets are community-curated. While we don&apos;t currently have a direct submission system, the best finds naturally make their way into the lists through community sharing and trending data." },
                  ].map((faq, idx) => (
                    <details key={idx} className="group bg-white rounded-2xl border border-background-200 overflow-hidden">
                      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-50 transition-colors">
                        <span className="text-sm font-semibold text-foreground-950 pr-4">{faq.q}</span>
                        <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                        <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                      </summary>
                      <div className="px-6 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* Section 7 - Conclusion */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Start Your Pandabuy Spreadsheet Journey Today
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-4">
                  The world of curated shopping is at your fingertips. Whether you&apos;re hunting for the latest sneaker drops,
                  building a capsule wardrobe on a budget, or just curious about what&apos;s trending — a well-organized{" "}
                  <strong>pandabuyspreadsheet</strong> is your best friend. No more endless scrolling, no more guesswork about quality,
                  and no more missing out on viral finds.
                </p>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  Head back to the{" "}
                  <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium underline">PandabuySpreadsheet homepage</Link> to
                  start exploring all 14 categories, or jump straight to the main store to browse the complete collection.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent-500 text-white text-sm font-semibold rounded-full hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap group"
                  >
                    Back to Homepage
                    <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-foreground-900 text-white text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap group"
                  >
                    Pandabuy Sheet
                    <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </article>

        {/* Related Categories */}
        <section className="py-16 bg-background-100">
          <div className="w-full px-6 md:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">Explore More Categories</h2>
                <p className="mt-2 text-sm text-foreground-500">Browse all curated pandabuy spreadsheet categories</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {categories.slice(0, 7).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border border-background-200 hover:border-accent-200 hover:bg-accent-50/30 transition-all cursor-pointer group"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 group-hover:bg-accent-100 transition-colors">
                    <i className={`${cat.icon} text-lg`}></i>
                  </span>
                  <span className="text-xs font-medium text-foreground-700 text-center whitespace-nowrap group-hover:text-foreground-950 transition-colors">
                    {cat.name}
                  </span>
                </a>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {categories.slice(7).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border border-background-200 hover:border-accent-200 hover:bg-accent-50/30 transition-all cursor-pointer group"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 group-hover:bg-accent-100 transition-colors">
                    <i className={`${cat.icon} text-lg`}></i>
                  </span>
                  <span className="text-xs font-medium text-foreground-700 text-center whitespace-nowrap group-hover:text-foreground-950 transition-colors">
                    {cat.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}